import React from 'react';
import { Helmet } from "react-helmet-async";
import { Accordion, Container, Row, Col, Button } from 'react-bootstrap';
import { FaShieldAlt, FaEye, FaLock, FaUserFriends } from 'react-icons/fa';

const principles = [
  {
    icon: <FaShieldAlt size={36} color="#4a90e2" />,
    title: 'Data Protection',
    description:
      'We implement industry-standard security measures to protect your personal information',
  },
  {
    icon: <FaEye size={36} color="#4a90e2" />,
    title: 'Transparency',
    description:
      'We clearly explain what data we collect and how we use it for our services',
  },
  {
    icon: <FaLock size={36} color="#4a90e2" />,
    title: 'Secure Storage',
    description:
      'Your data is encrypted and stored on secure servers with restricted access',
  },
  {
    icon: <FaUserFriends size={36} color="#4a90e2" />,
    title: 'Limited Sharing',
    description:
      'We never sell your data and only share it with trusted partners when necessary',
  },
];

const PrivacyPolicy = () => {
  return (
    <>
                <Helmet>
              <title>Privacy Policy || Paramount Green Services</title>
              <meta name="description" content="Free heating, insulation, and solar panel grants across the UK. Transform your home’s energy efficiency today." />
            </Helmet>
      {/* Header */}
      <header className="policy-header text-white text-center py-5">
        <Container>
          <h1 className="fw-bold mt-5">Privacy Policy</h1>
          <p className="lead mb-2">
            Your privacy is important to us. Learn how we protect and use your information.
          </p>
          <small>Last updated: October 01, 2025</small>
        </Container>
      </header>

      {/* Principles Section */}
      <section className="py-5">
        <Container>
          <h3 className="text-center fw-bold mb-3">Our Privacy Principles</h3>
          <p className="text-center mb-4 text-muted">
            We are committed to protecting your privacy through these core principles
          </p>
          <Row className="justify-content-center">
            {principles.map(({ icon, title, description }, idx) => (
              <Col key={idx} xs={12} sm={6} md={3} className="mb-4">
                <div className="principle-card p-3 h-100 border rounded text-center">
                  <div className="mb-3">{icon}</div>
                  <h6 className="fw-bold">{title}</h6>
                  <p className="small text-muted">{description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Accordion Content Section */}
     <section className="policy-content-section py-4">
        <Container>
          {/* Section 1 */}
          <div className="policy-section mb-4 p-4 border rounded bg-light">
            <h5 className="fw-bold mb-3 text-primary">1. Information We Collect</h5>
            <p className="fw-semibold">Personal Information</p>
            <p>
              When you use our services, we may collect personal information such as your name,
              email address, phone number, driver's license details, and payment information.
              This information is necessary to process your reservations and provide our services.
            </p>

            <p className="fw-semibold">Usage Information</p>
            <p>
              We collect information about how you use our website and services, including your
              browser type, IP address, pages visited, and interaction patterns. This helps us
              improve our services and user experience.
            </p>

            <p className="fw-semibold">Location Information</p>
            <p>
              For our courier and ride services, we may collect location information to provide
              accurate pickup and delivery services. This is only collected with your explicit
              consent.
            </p>
          </div>

          {/* Section 2 */}
          <div className="policy-section mb-4 p-4 border rounded bg-light">
            <h5 className="fw-bold mb-3 text-primary">2. How We Use Your Information</h5>
            <ul>
              <li>Process your reservations and provide the requested services</li>
              <li>Communicate with you about your bookings and service updates</li>
              <li>Process payments and prevent fraudulent transactions</li>
              <li>Improve our services based on usage patterns and feedback</li>
              <li>Send promotional offers and updates (with your consent)</li>
            </ul>
          </div>

          {/* Section 3 */}
          <div className="policy-section mb-4 p-4 border rounded bg-light">
            <h5 className="fw-bold mb-3 text-primary">3. Information Sharing</h5>
            <p className="fw-semibold">Service Providers</p>
            <p>
              We may share your information with trusted third-party service providers who help us
              operate our business, such as payment processors, insurance companies, and vehicle
              maintenance providers.
            </p>
            <p className="fw-semibold">Legal Requirements</p>
            <p>
              We may disclose your information if required by law or in response to valid legal
              requests from government authorities.
            </p>
            <p className="fw-semibold">Business Transfers</p>
            <p>
              In the event of a merger, acquisition, or sale of assets, your information may be
              transferred as part of the business assets.
            </p>
          </div>

          {/* Section 4 */}
          <div className="policy-section mb-4 p-4 border rounded bg-light">
            <h5 className="fw-bold mb-3 text-primary">4. Data Security</h5>
            <p>We implement robust security measures to protect your information. Our security includes:</p>
            <ul>
              <li>SSL encryption for all data transmission</li>
              <li>Secure data storage with access controls</li>
              <li>Regular security audits and updates</li>
              <li>Employee training on data privacy and security</li>
            </ul>
          </div>

          {/* Section 5 */}
          <div className="policy-section mb-4 p-4 border rounded bg-light">
            <h5 className="fw-bold mb-3 text-primary">5. Your Rights and Choices</h5>
            <ul>
              <li>You can request access to your personal information</li>
              <li>You can request corrections to inaccurate information</li>
              <li>You can request deletion of your personal information</li>
              <li>You can opt out of marketing communications</li>
              <li>You can request a copy of your data in a portable format</li>
            </ul>
          </div>

          {/* Section 6 */}
          <div className="policy-section mb-4 p-4 border rounded bg-light">
            <h5 className="fw-bold mb-3 text-primary">6. Cookies and Tracking</h5>
            <p>
              We use cookies and similar technologies to enhance your browsing experience, analyze
              website traffic, and personalize content. You can control cookie preferences through
              your browser settings.
            </p>
            <p className="fw-semibold">Types of Cookies We Use:</p>
            <ul>
              <li>Essential cookies for website functionality</li>
              <li>Analytics cookies to understand user behavior</li>
              <li>Marketing cookies for personalized advertising</li>
            </ul>
          </div>

          {/* Section 7 */}
          <div className="policy-section mb-4 p-4 border rounded bg-light">
            <h5 className="fw-bold mb-3 text-primary">7. Children’s Privacy</h5>
            <p>Protecting the privacy of minors</p>
            <p>
              Our services are not intended for children under 18 years of age. We do not knowingly
              collect personal information from children under 18. If we become aware that we have
              collected personal information from a child under 18, we will take steps to delete
              such information.
            </p>
          </div>

          {/* Section 8 */}
          <div className="policy-section mb-4 p-4 border rounded bg-light">
            <h5 className="fw-bold mb-3 text-primary">8. Contact Us</h5>
            <p>Questions about our privacy practices</p>
            <p>
              If you have any questions about this Privacy Policy or our privacy practices, please
              contact us:
            </p>
            <ul>
              <li>Email: <a href="mailto:paramountgreenservice@gmail.com" style={{textDecoration:"none"}}>paramountgreenservice@gmail.com</a></li>
              <li>Phone: (+92) 306-8629918</li>
              <li>Address: Bahria Town, Lahore - (53720)</li>
            </ul>
          </div>
        </Container>
      </section>


      {/* Call to Action */}
      <section className="cta-section text-center text-white py-5">
        <Container>
          <h4 className="fw-bold mb-3">Questions About Your Privacy?</h4>
          <p className="mb-3">
            We're committed to transparency and are happy to answer any privacy-related questions
          </p>
          <Button variant="light" size="sm">
            Contact Privacy Team
          </Button>
        </Container>
      </section>

    </>
  );
};

export default PrivacyPolicy;















